"use client";

import AnimatedCounter from "@/components/atoms/AnimatedCounter";
import RevealOnScroll from "@/components/atoms/RevealOnScroll";
// [RUDDERSTACK-OLD disabled 2026-07-20] widget_viewed → now central widget_impression
// (TrackedWidget in NewPage); see webapp/docs/analytics
// import { FireWidgetViewedEvent } from "@/customAnalytics/trackingEvents";
import { useIsMobile } from "@/hooks/useIsMobile";
import { cn } from "@/lib/utils";
import { hexToRgba } from "@/utils/utils";
import { useMemo, useRef } from "react";

const MAX_METRICS = 8;
const MAX_PRE_TITLE = 20;
const MAX_TITLE = 20;
const MAX_DESCRIPTION = 250;
const MAX_METRIC_TITLE = 7;
const MAX_METRIC_SUBTITLE = 30;

function clampStr(s: string, max: number) {
  return s.length <= max ? s : s.slice(0, max);
}

interface MetaItem {
  id: string | number;
  meta_title: string;
  meta_subtitle: string;
  meta_priority: number;
  meta_title_text_color?: string;
  meta_subtitle_text_color?: string;
  meta_status?: boolean;
}

interface MetricDisplayRow {
  item: MetaItem;
  title: string;
  subtitle: string;
}

function parseMetricTitleCounter(rawTitle: string): { end: number; suffix: string } {
  const text = String(rawTitle ?? "").trim();
  const match = text.match(/^(-?\d[\d,]*(?:\.\d+)?)(.*)$/);

  if (!match) {
    const fallback = Number(text.replace(/[^\d.-]/g, ""));
    return {
      end: Number.isFinite(fallback) ? fallback : 0,
      suffix: text.replace(/^[-\d.,\s]+/, "").trim(),
    };
  }

  const numericPart = Number(match[1].replace(/,/g, ""));
  return {
    end: Number.isFinite(numericPart) ? numericPart : 0,
    suffix: (match[2] || "").trim(),
  };
}

function MetricCell({
  item,
  title,
  subtitle,
  className,
  enableAnimation = true,
}: {
  item: MetaItem;
  title: string;
  subtitle: string;
  enableAnimation: boolean;
  className?: string;
}) {
  const { end, suffix } = parseMetricTitleCounter(title);

  return (
    <div className={cn("flex flex-col min-h-px min-w-0", className)}>
      <p
        className={cn(
          "leading-130 sm:leading-150",
          "text-[32px]",
          "sm:text-[30px]",
          "md:text-[36px]",
          "lg:text-[40px]"
        )}
        style={{
          color: item.meta_title_text_color ?? "#ffffff",
          fontFamily: "var(--font-merriweather)",
          fontWeight: 700,
        }}
      >
        {enableAnimation ? (
          <AnimatedCounter
            end={end}
            start={0}
            suffix={suffix ? ` ${suffix}` : ""}
            scrollSpyOnce={false}
          />
        ) : (
          `${end}${suffix ? ` ${suffix}` : ""}`
        )}
      </p>
      <p
        className={cn(
          "leading-140 tracking-[0.16px]",
          "text-[16px]",
          "sm:text-[14px] sm:tracking-[0.14px]",
          "lg:text-[16px] lg:tracking-[0.16px]"
        )}
        style={{
          color:
            item.meta_subtitle_text_color ?? "rgba(255, 255, 255, 0.8)",
          fontFamily: "var(--font-google-sans)",
          fontWeight: 500,
        }}
      >
        {subtitle}
      </p>
    </div>
  );
}

const KeyNumbers2 = ({ data }: { data: any }) => {
  const { data: sectionData } = data || {};
  const ref = useRef<HTMLElement>(null);
  const isMobile = useIsMobile(1080);

  if (!sectionData?.status) {
    return null;
  }

  const {
    desktop_background_color = "#FFFFFF",
    mobile_background_color = "#FFFFFF",
    backvariant = "variant_1",
    gradient_color_1 = "rgba(0, 212, 235, 0.33)",
    gradient_color_2 = "rgba(0, 0, 0, 0.66)",
    gradient_color_3 = "rgba(0, 0, 0, 0.66)",
    gradient_color_4 = "rgba(83, 69, 195, 0.46)",
    pre_title = "",
    pre_title_color = "rgba(255, 255, 255, 0.8)",
    title = "",
    title_start_color = "#FFFFFF",
    title_end_color = "#02A7B6",
    title_gradient_angle = "90deg",
    description = "",
    description_color = "rgba(255, 255, 255, 0.7)",
    metadata = [],
    enable_animation,
  } = sectionData;

  const isAnimationEnabled =
    enable_animation === true ||
    enable_animation === 1 ||
    enable_animation === "1";

  const sortedMetadata = useMemo(() => {
    if (!metadata || metadata.length === 0) return [];
    return [...metadata]
      .filter((item: MetaItem) => item?.meta_status !== false)
      .sort(
        (a: MetaItem, b: MetaItem) =>
          (a.meta_priority ?? 0) - (b.meta_priority ?? 0)
      );
  }, [metadata]);

  const displayMetrics = useMemo((): MetricDisplayRow[] => {
    return sortedMetadata.slice(0, MAX_METRICS).map((item: MetaItem) => ({
      item,
      title: clampStr(String(item.meta_title ?? ""), MAX_METRIC_TITLE),
      subtitle: clampStr(
        String(item.meta_subtitle ?? ""),
        MAX_METRIC_SUBTITLE
      ),
    }));
  }, [sortedMetadata]);

  const clampedPreTitle = useMemo(
    () => clampStr(String(pre_title ?? ""), MAX_PRE_TITLE),
    [pre_title]
  );
  const clampedTitle = useMemo(
    () => clampStr(String(title ?? ""), MAX_TITLE),
    [title]
  );
  const clampedDescription = useMemo(
    () => clampStr(String(description ?? ""), MAX_DESCRIPTION),
    [description]
  );

  const desktopGapClass = useMemo(() => {
    const n = displayMetrics.length;
    if (n <= 2) return "sm:gap-[40px]";
    if (n <= 4) return "sm:gap-[32px]";
    if (n <= 5) return "sm:gap-[20px]";
    if (n <= 6) return "sm:gap-[16px]";
    return "sm:gap-[12px]";
  }, [displayMetrics.length]);

  const toRgba = (color: string, fallbackOpacity: number): string => {
    if (!color) return `rgba(0, 0, 0, ${fallbackOpacity})`;
    if (color.startsWith("rgba(") || color.startsWith("rgb(")) return color;
    return hexToRgba(color, fallbackOpacity);
  };

  const getGradientBackground = () => {
    const g1 = toRgba(gradient_color_1, 0.33);
    const g2 = toRgba(gradient_color_2, 0.66);
    const g3 = toRgba(gradient_color_3, 0.66);
    const g4 = toRgba(gradient_color_4, 0.46);
    const base =
      isMobile === true ? mobile_background_color : desktop_background_color;
    switch (backvariant) {
      case "variant_1":
        return `linear-gradient(253deg, ${g1} 0%, ${g2} 24.68%, ${g3} 79.09%, ${g4} 100%), ${base}`;
      case "variant_2":
        return `linear-gradient(292deg, ${g1} 0%, ${g2} 24.68%, ${g3} 79.09%, ${g4} 100%), ${base}`;
      case "variant_3":
        return base;
      default:
        return `linear-gradient(253deg, ${g1} 0%, ${g2} 24.68%, ${g3} 79.09%, ${g4} 100%), ${base}`;
    }
  };

  const titleGradientStyle: React.CSSProperties = {
    ...(title_start_color && title_end_color
      ? {
          backgroundImage: `linear-gradient(${title_gradient_angle}, ${title_start_color}, ${title_end_color})`,
          WebkitBackgroundClip: "text",
          backgroundClip: "text",
          WebkitTextFillColor: "transparent",
        }
      : {
          color: title_start_color || "#ffffff",
        }),
    fontFamily: "var(--font-merriweather)",
  };

  // [RUDDERSTACK-OLD disabled 2026-07-20] impression now central; see webapp/docs/analytics
  // useEffect(() => {
  //   if (ref.current) {
  //     FireWidgetViewedEvent(ref, "key_numbers_section");
  //   }
  // }, []);

  if (isMobile === null) {
    return null;
  }

  const metricCount = displayMetrics.length;

  return (
    <section
      ref={ref}
      className="relative w-full overflow-hidden"
      style={{
        backgroundColor:
          isMobile === true
            ? mobile_background_color
            : desktop_background_color,
      }}
    >
      <style dangerouslySetInnerHTML={{ __html: `
        @media (min-width: 1748px) {
          .key-numbers-2-container {
            max-width: 1750px !important;
            padding-left: 160px !important;
            padding-right: 160px !important;
            padding-top: 80px !important;
            padding-bottom: 80px !important;
          }
        }
      ` }} />
      <div
        className="absolute inset-0 z-0"
        style={{ background: getGradientBackground() }}
      />
      <div
        className={cn(
          "relative z-10 mx-auto flex flex-col key-numbers-2-container",
          "px-[20px] py-[40px] gap-[20px]",
          "sm:max-w-[1080px] sm:px-[60px] sm:py-[40px] sm:gap-[20px]",
          "md:max-w-[1230px] md:px-[80px] md:py-[60px] md:gap-[24px]",
          "lg:max-w-[1430px] lg:px-[100px] lg:py-[60px]",
          "xl:max-w-[1750px] xl:px-[160px] xl:py-[80px]"
        )}
      >
        <div className="flex flex-col gap-[4px] sm:gap-[2px]">
          {clampedPreTitle && (
            <RevealOnScroll enableAnimation={isAnimationEnabled}>
              <p
                className={cn(
                  "leading-normal",
                  "text-[24px]",
                  "sm:text-[22px]",
                  "md:text-[26px]",
                  "lg:text-[30px]"
                )}
                style={{
                  color: pre_title_color,
                  fontFamily: "var(--font-google-sans)",
                  fontWeight: 600,
                }}
              >
                {clampedPreTitle}
              </p>
            </RevealOnScroll>
          )}
          {clampedTitle && (
            <RevealOnScroll enableAnimation={isAnimationEnabled}>
              <p
                className={cn(
                  "leading-120",
                  "text-[40px]",
                  "sm:text-[46px]",
                  "md:text-[56px]",
                  "lg:text-[64px]"
                )}
                style={titleGradientStyle}
              >
                {clampedTitle}
              </p>
            </RevealOnScroll>
          )}
        </div>

        {clampedDescription && (
          <RevealOnScroll enableAnimation={isAnimationEnabled}>
            <p
              className={cn(
                "leading-[1.49]",
                "text-[12px]",
                "sm:min-w-full sm:w-min sm:text-[14px]",
                "md:text-[16px]",
                "lg:text-[18px]"
              )}
              style={{
                color: description_color,
                fontFamily: "var(--font-google-sans)",
                fontWeight: 600,
              }}
            >
              {clampedDescription}
            </p>
          </RevealOnScroll>
        )}
  
        {metricCount > 0 && (
          <>
            <div className="flex flex-col gap-[32px] w-full sm:hidden">
              {metricCount <= 4 ? (
                displayMetrics.map(({ item, title: t, subtitle: st }, index) => (
                  <RevealOnScroll
                    key={item.id ?? `kn2-m-${index}`}
                    enableAnimation={isAnimationEnabled}
                    className="w-full"
                  >
                    <MetricCell
                      item={item}
                      title={t}
                      subtitle={st}
                      enableAnimation={isAnimationEnabled}
                      className="w-full"
                    />
                  </RevealOnScroll>
                ))
              ) : (
                <>
                  {displayMetrics.slice(0, 3).map(({ item, title: t, subtitle: st }, index) => (
                    <RevealOnScroll
                      key={item.id ?? `kn2-mh-${index}`}
                      enableAnimation={isAnimationEnabled}
                      className="w-full"
                    >
                      <MetricCell
                        item={item}
                        title={t}
                        subtitle={st}
                        enableAnimation={isAnimationEnabled}
                        className="w-full"
                      />
                    </RevealOnScroll>
                  ))}
                  <div className="grid grid-cols-2 gap-x-6 gap-y-[32px] w-full">
                    {displayMetrics.slice(3).map(({ item, title: t, subtitle: st }, index) => (
                      <RevealOnScroll
                        key={item.id ?? `kn2-mg-${index + 3}`}
                        enableAnimation={isAnimationEnabled}
                        className="min-w-0"
                      >
                        <MetricCell
                          item={item}
                          title={t}
                          subtitle={st}
                          enableAnimation={isAnimationEnabled}
                          className="min-w-0"
                        />
                      </RevealOnScroll>
                    ))}
                  </div>
                </>
              )}
            </div>

            <div
              className={cn(
                "hidden sm:flex sm:flex-row sm:flex-nowrap sm:w-full",
                "sm:justify-start sm:items-start",
                desktopGapClass
              )}
            >
              {displayMetrics.map(({ item, title: t, subtitle: st }, index) => (
                <RevealOnScroll
                  key={item.id ?? `kn2-d-${index}`}
                  enableAnimation={isAnimationEnabled}
                  className="sm:flex-1 sm:basis-0 sm:min-w-0"
                >
                  <MetricCell
                    item={item}
                    title={t}
                    subtitle={st}
                    enableAnimation={isAnimationEnabled}
                    className="sm:flex-1 sm:basis-0 sm:min-w-0"
                  />
                </RevealOnScroll>
              ))}
            </div>
          </>
        )}
      </div>
    </section>
  );
};

export default KeyNumbers2;
